import React from "react";

/**
 * GlitchLogo — a distinctive blue orb with a live audio waveform pulsing through it.
 * Used as the app-level mark on login + sidebar. Accepts a size in px.
 */
export const GlitchLogo = ({ size = 48, animated = true, className = "" }) => {
  const s = size;
  const uid = React.useId();
  return (
    <svg
      viewBox="0 0 64 64"
      width={s}
      height={s}
      className={className}
      role="img"
      aria-label="Glitch"
    >
      <defs>
        <radialGradient id={`orb-${uid}`} cx="35%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#dbeafe" />
          <stop offset="35%" stopColor="#60a5fa" />
          <stop offset="70%" stopColor="#2563eb" />
          <stop offset="100%" stopColor="#0b1e4b" />
        </radialGradient>
        <linearGradient id={`ring-${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#93c5fd" />
          <stop offset="100%" stopColor="#1e40af" />
        </linearGradient>
        <filter id={`glow-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="2.2" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Outer aura */}
      <circle cx="32" cy="32" r="30" fill={`url(#orb-${uid})`} opacity="0.28" />

      {/* Rotating dashed ring */}
      <g style={{ transformOrigin: "32px 32px" }}>
        {animated && (
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 32 32"
            to="360 32 32"
            dur="14s"
            repeatCount="indefinite"
          />
        )}
        <circle
          cx="32"
          cy="32"
          r="27"
          fill="none"
          stroke={`url(#ring-${uid})`}
          strokeWidth="1.2"
          strokeDasharray="3 6"
          opacity="0.75"
        />
      </g>

      {/* Core orb */}
      <circle
        cx="32"
        cy="32"
        r="20"
        fill={`url(#orb-${uid})`}
        filter={`url(#glow-${uid})`}
      />

      {/* Sound bars */}
      <g fill="#f8fafc">
        <rect x="19" y="30" width="2.4" height="4" rx="1.2">
          {animated && (
            <animate
              attributeName="height"
              values="4;10;4"
              dur="1.1s"
              repeatCount="indefinite"
              additive="sum"
            />
          )}
          {animated && (
            <animate
              attributeName="y"
              values="30;27;30"
              dur="1.1s"
              repeatCount="indefinite"
              additive="sum"
            />
          )}
        </rect>
        <rect x="24" y="26" width="2.4" height="12" rx="1.2">
          {animated && (
            <animate
              attributeName="height"
              values="12;20;12"
              dur="1.3s"
              repeatCount="indefinite"
              additive="sum"
            />
          )}
          {animated && (
            <animate
              attributeName="y"
              values="26;22;26"
              dur="1.3s"
              repeatCount="indefinite"
              additive="sum"
            />
          )}
        </rect>
        <rect x="29" y="22" width="2.4" height="20" rx="1.2">
          {animated && (
            <animate
              attributeName="height"
              values="20;28;20"
              dur="0.9s"
              repeatCount="indefinite"
              additive="sum"
            />
          )}
          {animated && (
            <animate
              attributeName="y"
              values="22;18;22"
              dur="0.9s"
              repeatCount="indefinite"
              additive="sum"
            />
          )}
        </rect>
        <rect x="34" y="26" width="2.4" height="12" rx="1.2">
          {animated && (
            <animate
              attributeName="height"
              values="12;20;12"
              dur="1.4s"
              repeatCount="indefinite"
              additive="sum"
            />
          )}
          {animated && (
            <animate
              attributeName="y"
              values="26;22;26"
              dur="1.4s"
              repeatCount="indefinite"
              additive="sum"
            />
          )}
        </rect>
        <rect x="39" y="30" width="2.4" height="4" rx="1.2">
          {animated && (
            <animate
              attributeName="height"
              values="4;10;4"
              dur="1.2s"
              repeatCount="indefinite"
              additive="sum"
            />
          )}
          {animated && (
            <animate
              attributeName="y"
              values="30;27;30"
              dur="1.2s"
              repeatCount="indefinite"
              additive="sum"
            />
          )}
        </rect>
      </g>

      {/* Highlight */}
      <ellipse cx="26" cy="24" rx="6" ry="3.5" fill="#ffffff" opacity="0.22" />
    </svg>
  );
};

/** Compact monogram — shows the first letter of the assistant's name inside a small orb tile. */
export const AssistantMark = ({ name = "Glitch", size = 32 }) => {
  const letter = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div
      className="rounded-lg bg-gradient-to-br from-blue-400 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/30"
      style={{ width: size, height: size }}
      aria-hidden
    >
      <span
        className="font-display font-bold text-white"
        style={{ fontSize: size * 0.5 }}
      >
        {letter}
      </span>
    </div>
  );
};
